import {EventEmitter, Injectable} from '@angular/core';
import {IPlatformToolHandler} from '@shared/components/platform-tool/platform-tool-container/platform-tool-component';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {MagicLinkModel, TabModel} from '@shared/models/platform-tool-models';
import {PlatformCommand} from '@shared/commands/command-base';
import {filter, take} from 'rxjs/operators';
import {CommandService} from '@core/services/command.service';
import {NavigationEnd, Router} from '@angular/router';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';
import {SnackBarService} from '@core/services/snack-bar.service';
import {Api} from "@shared/constants/common-field.constants";
import {AuthApiService} from "@core/services/auth-api.service";
import {ActionsSubject} from '@ngrx/store';

/** The "[OTHERS] <EventType>" action the websocket layer dispatches for the tile-counts push. */
export const WORKSPACE_TILE_COUNTS_UPDATED = '[OTHERS] WorkspaceTileCountsUpdated';

@Injectable({
  providedIn: 'root',
})

export class WorkspaceHandler implements IPlatformToolHandler {
  invalidateSubject: InvalidateSubject;
  magicLinkModel: MagicLinkModel;
  magicLinkModelValueChanged: EventEmitter<any> = new EventEmitter<any>();

  private previousUrl: string = null;
  private currentUrl: string = null;

  constructor(private commandService: CommandService,
              private router: Router,
              private authApiService: AuthApiService,
              private fuseSplashScreenService: FuseSplashScreenService,
              private snackBarService: SnackBarService,
              private actions$: ActionsSubject) {

    this.currentUrl = this.router.url;

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.previousUrl = this.currentUrl;
        this.currentUrl = event.url;
      });

    // WS-E-07-15: the API pushes "WorkspaceTileCountsUpdated" once a Manage Data transfer reaches
    // Ready (WorkspacePlatformLinkUpdateCommandConsumer). If the pushed counts belong to the
    // details page currently on screen, patch its tiles in place -- previously only a full
    // nav-away-and-back showed the transferred data.
    this.actions$
      .pipe(filter((action: any) => action.type === WORKSPACE_TILE_COUNTS_UPDATED))
      .subscribe((action: any) => this.applyLiveTileCounts(action.payload?.Payload));
  }

  /**
   * In-place tile refresh for an already-open Workspace Details page: writes the pushed counts into
   * the detail tab's tabData (the tile grid reads them back as its request parameters), rebroadcasts
   * the patched model so the grid re-caches those parameters, then invalidates so it re-fetches.
   * Same patch-don't-reload idea as the RefreshPlatformLink pattern, driven by an async push.
   */
  applyLiveTileCounts(counts: any): void {
    if (!counts || !this.magicLinkModel || this.magicLinkModel.id !== counts.platformLinkId) {
      return;
    }
    const detailTab = this.magicLinkModel.tabBar?.tabs?.find(
      tab => tab.tabData && tab.tabData['SourceType'] === 'Workspace-Detail-View');
    if (!detailTab) {
      return;
    }
    detailTab.tabData['EntityCount'] = counts.entityCount;
    detailTab.tabData['TransactionCount'] = counts.transactionCount;
    detailTab.tabData['ScenarioCount'] = counts.scenarioCount;
    detailTab.tabData['FilingManagerCount'] = counts.filingManagerCount;

    this.magicLinkModelValueChanged.emit({
      id: 'PlatformToolVirtualGridComponent',
      magicLinkModel: this.magicLinkModel,
      selectedTab: detailTab
    });
    this.invalidateSubject?.invalidate();
  }

  destroy(): void {
    // Called on every page teardown, but this is a root singleton: its router and tile-push subscriptions live as long as the app.
  }

  executeCommand(command: PlatformCommand, callBack?: any) {
    this.executeCommandToApi(command);
  }
  private executeCommandToApi(command: PlatformCommand) {
    if (command.api && command.api.length > 0 && !command.isDisabled) {
      this.fuseSplashScreenService.show();
      this.commandService.executePlatformCommand(command).pipe(take(1)).subscribe(commandResponse => {
          if (commandResponse.fieldValues['RedirectUrl']) {
            this.router.navigateByUrl(commandResponse.fieldValues['RedirectUrl']);
          }
          if (commandResponse.fieldValues['RedirectCommand']) {
            this.executeCommandToApi(commandResponse.fieldValues['RedirectCommand']);
            return;
          }
          if (commandResponse.fieldValues['SnackBarMessage']) {
            const duration = commandResponse.fieldValues['SnackBarMessage'].length * 80;
            this.snackBarService.showInformation(commandResponse.fieldValues['SnackBarMessage'], duration);
          }
          if (commandResponse.fieldValues['ErrorMessage']) {
            this.snackBarService.showError(commandResponse.fieldValues['ErrorMessage']);
          }
          if (commandResponse.fieldValues['ReloadUrl']) {
            if (commandResponse.fieldValues['LogOut']) {
              // logoutUser navigates once the sign-out is done; navigating here too raced it (device_disabled).
              this.authApiService.logoutUser(commandResponse.fieldValues['ReloadUrl']);
              return;
            }
            this.replaceLocation(commandResponse.fieldValues['ReloadUrl']);
          }
          if (commandResponse.fieldValues['PlatformLinkModel']) {
            this.invalidateSubject.invalidate('Refresh');
          } else {
            this.invalidateSubject.invalidate();
          }

          this.fuseSplashScreenService.hide();
        },
        error => {
          console.log(error);
          this.fuseSplashScreenService.hide();
        });
    }
  }

  private replaceLocation(url: string): void {
    window.location.replace(url);
  }

  setSelectedTab(selectedTab: TabModel): void {
    // previousUrl stays null until the first navigation after the handler is created.
    if(this.previousUrl?.startsWith("/workspace"))
      return;

    if(this.magicLinkModel?.fieldValues?.["ShowWorkspaceDescriptionDialog"]) {
      const descriptionDialogCommand = new PlatformCommand("ShowWorkspaceDescriptionCommand", Api.AdminPanel);
      descriptionDialogCommand.fieldValues["PlatformDialogId"] = this.magicLinkModel.fieldValues["WorkspacePlatformDialogId"]
      this.executeCommand(descriptionDialogCommand);
    }
  }

  valueChanged(event) {
    if (event.name) {
      const command = new PlatformCommand(event.name, event.api);
      command.fieldValues = event.fieldValues;
      command.waitForResponse = true;
      command.projectId = '[PROJECT_ID]';
      this.executeCommand(command);
    } else {
      const fieldKey = event.fieldDefinition.key;
      if (this.magicLinkModel.additionalCommands[`${fieldKey}LinkCommand`]) {
        const command = this.magicLinkModel.additionalCommands[`${fieldKey}LinkCommand`];
        command.fieldValues['ItemId'] = event.item.id;
        command.projectId = '[PROJECT_ID]';
        this.executeCommand(command);
      }
    }


  }

}
